import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { withLatestFrom, tap, concatMap, catchError, EMPTY } from "rxjs";
import { ProfileLanguageService } from "../../services/profile-language-service";
import { AuthAccountApiService } from "../../services/api-services/auth-account/auth-account.api-service";
import { AuthTokenService } from "../../services/auth-token.service";
import { SnackbarService } from "../../services/snackbar.service";
import { ProfileStateAction } from "./profile-state.actions";
import { profileSelectors } from "./profile-state.selector";


@Injectable()
export class ProfileStateEffect {

  actions$: Actions = inject(Actions);
  store: Store = inject(Store);
  translation: TranslateService = inject(TranslateService);
  profileLanguageService: ProfileLanguageService = inject(ProfileLanguageService);
  authAccountApi: AuthAccountApiService = inject(AuthAccountApiService);
  oidcSecurityService: OidcSecurityService = inject(OidcSecurityService);
  tokenService: AuthTokenService = inject(AuthTokenService);
  snackbar: SnackbarService = inject(SnackbarService);

  setLanguageInTranslationService$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProfileStateAction.setLanguage),  // Listen for a specific action
        withLatestFrom(this.store.select(profileSelectors.selectLanguage)),  // Get the latest state value
        tap(([action, language]) => {
          this.profileLanguageService.setLanguage(language);
          this.translation.use(this.profileLanguageService.language());
        })
      ),
    { dispatch: false }  // Set dispatch to false to prevent any action from being dispatched
  );

  // concatMap so the last pick is also the last one saved.
  saveLanguage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProfileStateAction.saveLanguage),
        concatMap(({ language }) => this.authAccountApi.saveLanguage(language).pipe(
          catchError(() => {
            this.snackbar.error('PROFILE.LANGUAGE_SAVE_FAILED');
            return EMPTY;
          }),
          // The startup language comes from the access token; refresh it now so a reload keeps the pick.
          concatMap(() => this.oidcSecurityService.forceRefreshSession().pipe(catchError(() => EMPTY))),
          tap(result => {
            if (result?.accessToken) {
              this.tokenService.setToken(result.accessToken);
            }
          })
        ))
      ),
    { dispatch: false }
  );

}
